import type { workforce } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure workforce-map derivations (Track A). DOM-free and unit-tested: the
 * filter predicate, status→tone mapping, and calendar-day helpers. The screens
 * render these results; none of the reasoning lives inline in JSX.
 */

export type StatusFilter = 'all' | 'active' | 'inactive' | 'terminated';

export interface WorkforceFilters {
  query: string;
  role: string;
  program: string;
  status: StatusFilter;
  /** Only workers with an open session right now. */
  onMapOnly: boolean;
}

export const EMPTY_FILTERS: WorkforceFilters = {
  query: '',
  role: '',
  program: '',
  status: 'all',
  onMapOnly: false,
};

export function matchesFilters(
  worker: workforce.WorkforceWorker,
  filters: WorkforceFilters,
  hiddenIds: ReadonlySet<string>,
): boolean {
  if (hiddenIds.has(worker.workerId)) return false;
  if (filters.onMapOnly && !worker.hasOpenSession) return false;
  if (filters.status !== 'all' && worker.employmentStatus !== filters.status) return false;
  if (filters.role !== '' && (worker.roleTitle ?? '') !== filters.role) return false;
  if (filters.program !== '' && (worker.programName ?? '') !== filters.program) return false;
  const q = filters.query.trim().toLowerCase();
  if (q !== '' && !worker.displayName.toLowerCase().includes(q)) return false;
  return true;
}

/**
 * Narrow the map's Site layer to one site, or leave it whole.
 *
 * A Site filter is about the PLACE layer, not the people layer: choosing a site
 * focuses the map on that site and its fence without silently hiding workers who
 * are somewhere else, because "show me St. Cloud" should not quietly claim
 * nobody else is working today.
 */
export function visibleSites<T extends { siteId: string }>(
  sites: readonly T[],
  siteId: string,
): T[] {
  return siteId === '' ? [...sites] : sites.filter((s) => s.siteId === siteId);
}

/**
 * The geofences to draw, given the Site filter.
 *
 * Fences that belong to no site (ad-hoc hubs, partner spots) are ALWAYS kept:
 * they still evaluate clock-ins, and dropping them when a site is selected would
 * make a punch look unexplained.
 */
export function visibleFences<T extends { siteId: string | null }>(
  fences: readonly T[],
  siteId: string,
): T[] {
  return siteId === ''
    ? [...fences]
    : fences.filter((f) => f.siteId === siteId || f.siteId == null);
}

/** Distinct role titles (for the filter dropdown), sorted, nulls → 'No role'. */
export function distinctRoles(workers: readonly workforce.WorkforceWorker[]): string[] {
  const set = new Set<string>();
  for (const w of workers) set.add(w.roleTitle ?? 'No role');
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Distinct program names (for the filter dropdown), sorted, nulls → 'No program'. */
export function distinctPrograms(workers: readonly workforce.WorkforceWorker[]): string[] {
  const set = new Set<string>();
  for (const w of workers) set.add(w.programName ?? 'No program');
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Geofence status → pill tone. Never invents a verdict: indeterminate = unsure. */
export function geofenceTone(status: string): Severity {
  if (status === 'inside') return 'good';
  if (status === 'outside') return 'look';
  return 'unsure';
}

/** Session status → pill tone for the worker's session list. */
export function sessionTone(session: workforce.WorkforceSessionSummary): Severity {
  if (session.openNow) return 'good';
  if (session.autoClosed) return 'look';
  return 'plain';
}

/** Local calendar date as `YYYY-MM-DD`. */
export function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Shift a `YYYY-MM-DD` date by whole local days (DST-safe: local-midnight math). */
export function addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n);
  return toLocalDateString(dt);
}

/** The browser's IANA timezone, falling back to 'UTC' when unavailable. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// --- the route-history panel ------------------------------------------------

/**
 * What the route-history panel under a selected session is showing.
 *
 * `refused` is a state of its own and it is FIRST, which is the whole point.
 * The timeline route needs two keys and this screen's door needs one of them,
 * so a reader holding the map key and not the trail key reaches the panel and
 * is refused by the server. Rendered through the query's own `isError` branch,
 * that permanent refusal came out as "We couldn't load this route just now"
 * with a Try again — the shape of a transient failure. Every retry returns the
 * same 403, so the reader concludes the product is broken rather than that
 * their account is scoped, and never thinks to ask for the key.
 *
 * Ordering it above `requested` / `isLoading` / `isError` means no read state
 * can describe a refusal, and no refusal can be mistaken for a read that has
 * not happened yet. `failed` still exists and still offers a retry, for the
 * case where retrying is the right answer: a reader who holds BOTH keys and
 * met a network error.
 */
export type SessionTrailState = 'refused' | 'prompt' | 'loading' | 'failed' | 'ready';

export interface SessionTrailInput {
  /**
   * `mayReadSessionTrail(usePermissions())` — BOTH keys the timeline route
   * requires, each over the ORGANIZATION, because that is the scope both of its
   * gates name.
   */
  canReadTrail: boolean;
  /** The audited read has been asked for: a click, or a link naming a session. */
  requested: boolean;
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
}

export function sessionTrailState(input: SessionTrailInput): SessionTrailState {
  if (!input.canReadTrail) return 'refused';
  if (!input.requested) return 'prompt';
  if (input.isLoading) return 'loading';
  if (input.isError) return 'failed';
  return input.hasData ? 'ready' : 'loading';
}

/**
 * Whether to actually FIRE the timeline read.
 *
 * The same permission fact as `sessionTrailState`, applied to the query's
 * `enabled` rather than to what is drawn. Both are needed: hiding the panel
 * without this leaves a deep link carrying `?session=` firing a read already
 * known to be refused, which puts a 403 in the network log, caches a rejection
 * under the query key, and sends a reason-for-access header for a reveal
 * nobody asked for.
 */
export function shouldReadSessionTrail(
  selectedSessionId: string | null,
  requested: boolean,
  canReadTrail: boolean,
): boolean {
  return selectedSessionId != null && requested && canReadTrail;
}
